import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import GolfStats from './GolfStats'

export default function GolfProfile({ session, onComplete, onSkip }) {
  const [golfProfile, setGolfProfile] = useState(null)
  const [editing, setEditing] = useState(false)
  const [handicap, setHandicap] = useState('')
  const [ghin, setGhin] = useState('')
  const [homeCourse, setHomeCourse] = useState('')
  const [method, setMethod] = useState('manual')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => { load() }, [session])

  const load = async () => {
    const { data: g } = await supabase.from('golf_profiles').select('*').eq('id', session.user.id).single()
    if (g) {
      setGolfProfile(g)
      setHandicap(g.handicap_index != null ? String(g.handicap_index) : '')
      setGhin(g.ghin_number || '')
      setHomeCourse(g.home_course || '')
      setMethod(g.ghin_number ? 'ghin' : g.hcp_type === 'crossbet' ? 'crossbet' : 'manual')
    } else {
      setEditing(true)
    }
    setLoading(false)
  }

  const handleSave = async () => {
    setError(null)
    if (method === 'manual' && handicap === '') return setError('Enter your handicap index')
    if (method === 'ghin' && !ghin) return setError('Enter your GHIN number')
    const hcp = method !== 'crossbet' && handicap !== '' ? parseFloat(handicap) : golfProfile?.handicap_index || null
    if (method === 'manual' && hcp !== null && (isNaN(hcp) || hcp < -10 || hcp > 54)) return setError('Enter a valid handicap between -10 and 54')
    setSaving(true)
    const { error: e } = await supabase.from('golf_profiles').upsert({
      id: session.user.id,
      handicap_index: hcp,
      ghin_number: method === 'ghin' ? ghin : null,
      home_course: homeCourse || null,
      hcp_type: method === 'ghin' ? 'ghin' : method === 'crossbet' ? 'crossbet' : 'manual',
    })
    setSaving(false)
    if (e) return setError(e.message)
    setSuccess('Saved ✓')
    setEditing(false)
    load()
    if (onComplete) setTimeout(() => onComplete(), 500)
  }

  const hcpColor = !golfProfile ? '#7A7570' : golfProfile.hcp_type === 'ghin' ? '#00563F' : golfProfile.hcp_type === 'crossbet' ? '#C19A6B' : '#7A7570'
  const hcpLabel = !golfProfile ? 'Manual' : golfProfile.hcp_type === 'ghin' ? 'GHIN' : golfProfile.hcp_type === 'crossbet' ? 'CROSSBET' : 'Manual'

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#7A7570', fontFamily: 'DM Sans, sans-serif' }}>Loading...</div>
    </div>
  )

  return (
    <div style={s.screen}>
      <div style={s.body}>

        {/* Profile card — display mode */}
        {!editing && golfProfile && (
          <div style={s.card}>
            <div style={s.row}>
              <div>
                <div style={s.label}>HANDICAP INDEX</div>
                <div style={s.hcpRow}>
                  <span style={s.hcpNum}>{golfProfile.handicap_index != null ? golfProfile.handicap_index : '—'}</span>
                  <span style={{ ...s.badge, color: hcpColor, background: hcpColor + '18', border: `1px solid ${hcpColor}44` }}>{hcpLabel}</span>
                </div>
              </div>
              <button style={s.editBtn} onClick={() => { setEditing(true); setSuccess(null) }}>Edit</button>
            </div>

          </div>
        )}

        {/* Edit form */}
        {editing && (
          <div style={s.card}>
            <div style={s.label}>HANDICAP METHOD</div>
            <div style={s.methodRow}>
              {[
                { key: 'manual', label: '✏️ Manual' },
                { key: 'ghin', label: '🏅 GHIN' },
                { key: 'crossbet', label: '⚡ CROSSBET' },
              ].map(m => (
                <button key={m.key} onClick={() => setMethod(m.key)}
                  style={{ ...s.methodBtn, ...(method === m.key ? s.methodBtnActive : {}) }}>
                  {m.label}
                </button>
              ))}
            </div>

            {method === 'manual' && (
              <input style={s.input} type="number" step="0.1" min="-10" max="54"
                placeholder="Handicap Index — e.g. 12.4 · 0 for scratch"
                value={handicap} onChange={e => setHandicap(e.target.value)} />
            )}
            {method === 'ghin' && (
              <input style={s.input} placeholder="GHIN Number — e.g. 1234567"
                value={ghin} onChange={e => setGhin(e.target.value)} />
            )}
            {method === 'crossbet' && (
              <div style={s.infoNote}>
                CROSSBET calculates your handicap automatically from your completed rounds. Requires 8 valid rounds.
                {golfProfile?.valid_rounds > 0 ? ` You have ${golfProfile.valid_rounds}/8.` : ''}
              </div>
            )}

            <input style={s.input} placeholder="Home Course (optional) — e.g. Doral Golf Resort"
              value={homeCourse} onChange={e => setHomeCourse(e.target.value)} />

            {error && <div style={s.error}>{error}</div>}

            <div style={s.btnRow}>
              <button style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              {golfProfile && (
                <button style={s.cancelBtn} onClick={() => { setEditing(false); setError(null) }}>Cancel</button>
              )}
            </div>
          </div>
        )}

        {/* Home Course card */}
        {!editing && golfProfile && (
          <div style={s.card}>
            <div style={s.row}>
              <div>
                <div style={s.label}>HOME COURSE</div>
                <div style={s.courseVal}>
                  {golfProfile.home_course || <span style={{color:'#B0ACA4',fontStyle:'italic'}}>No home course set</span>}
                </div>
              </div>
              <button style={s.editBtn} onClick={() => { setEditing(true); setSuccess(null) }}>Edit</button>
            </div>
          </div>
        )}

        {success && !editing && <div style={s.successMsg}>{success}</div>}
        {onSkip && !golfProfile && <button style={s.skipBtn} onClick={onSkip}>Skip for now</button>}

        {/* Stats divider */}
        <div style={s.statsDivider}>
          <div style={s.dividerLine} />
          <div style={s.dividerLabel}>GOLF STATS</div>
          <div style={s.dividerLine} />
        </div>

        <GolfStats session={session} embedded={true} />

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  body: { padding: '16px 24px 48px', display: 'flex', flexDirection: 'column', gap: 12 },
  card: { background: '#FFFFFF', borderRadius: 20, padding: '14px 18px', border: '1px solid #E0DDD4', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 8 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  label: { fontSize: 10, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4 },
  hcpRow: { display: 'flex', alignItems: 'center', gap: 8 },
  hcpNum: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: '#1A1814', lineHeight: 1 },
  badge: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px' },
  editBtn: { background: 'transparent', border: '1px solid #00563F', borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700, color: '#00563F', cursor: 'pointer', fontFamily: 'inherit' },
  dividerRow: { display: 'flex', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: '1px solid #EDE8D8' },
  courseVal: { fontSize: 13, color: '#1A1814', fontWeight: 500 },
  methodRow: { display: 'flex', gap: 6 },
  methodBtn: { flex: 1, padding: '8px 4px', background: '#FDF9E7', border: '1.5px solid #E0DDD4', borderRadius: 10, fontSize: 12, fontWeight: 600, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  methodBtnActive: { background: 'rgba(0,96,57,0.06)', border: '1.5px solid #00563F', color: '#00563F' },
  input: { background: '#FDF9E7', border: '1.5px solid #D0CCC0', borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  infoNote: { fontSize: 12, color: '#7A7570', background: 'rgba(201,168,76,0.08)', borderRadius: 10, padding: '10px 12px', lineHeight: 1.5 },
  error: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#C0392B' },
  btnRow: { display: 'flex', gap: 8 },
  saveBtn: { flex: 1, background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  cancelBtn: { background: 'transparent', border: '1.5px solid #E0DDD4', borderRadius: 10, padding: '12px 18px', fontSize: 14, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit' },
  successMsg: { background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#00563F', fontWeight: 600 },
  skipBtn: { background: 'transparent', color: '#7A7570', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  statsDivider: { display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' },
  dividerLine: { flex: 1, height: 1, background: '#E0DDD4' },
  dividerLabel: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 2 },
}
